import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock, ShieldCheck, FileCheck, Sparkles, CheckCircle2, Building2, FlaskConical, Receipt } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import JsonLd from "../components/JsonLd";
import { usePageSEO } from "../hooks/usePageSEO";
import { AFFILIATE_URLS, onAffiliateClick } from "../config/affiliate";

const BASE = "https://www.peptidesformedspas.org";
const APPLY_URL = AFFILIATE_URLS.providerAccess;
const TRACK = (loc) => onAffiliateClick({ partner: "alphabiomedlabs", product: `provider-access-${loc}`, url: APPLY_URL });

const BENEFITS = [
  {
    icon: Receipt,
    title: "Institutional pricing tiers",
    body:
      "Multi-unit pricing structured for clinics, practices, and licensed research operations. Per-unit cost drops materially as order volume increases — meaningful savings versus single-unit retail.",
  },
  {
    icon: Lock,
    title: "Authorized account credentials",
    body:
      "Verified-provider accounts unlock catalog visibility, account history, and reorder workflows that aren't available on the public-facing storefront.",
  },
  {
    icon: FileCheck,
    title: "Documentation on request",
    body:
      "Where available, batch-level Certificates of Analysis (COA), HPLC purity data, and MS identity confirmation can be requested for a specific lot.",
  },
  {
    icon: ShieldCheck,
    title: "Research-only product class",
    body:
      "All materials are research chemicals supplied for laboratory research purposes only. Not for human or animal use. Not FDA-approved drugs.",
  },
];

const ELIGIBLE = [
  "Med spas operating under licensed medical direction",
  "Aesthetic and longevity clinics with licensed clinicians on staff",
  "Compounding pharmacy partners (503A / 503B context, where applicable)",
  "Telehealth platforms with licensed prescribers",
  "Academic and contract research labs",
  "Wellness practices structured under their state's medical-direction rules",
];

const HOW_IT_WORKS = [
  { n: "01", title: "Apply", body: "Click 'Apply for Provider Access' and complete the Alpha BioMed Labs verification form. Plan ~5 minutes." },
  { n: "02", title: "Verify", body: "Alpha BioMed Labs reviews your practice/research credentials and confirms eligibility for the verified-provider tier." },
  { n: "03", title: "Order", body: "Once verified, log in to see authorized pricing, multi-unit tiers, and your account dashboard. Reorder, request COAs, and manage shipping from one place." },
];

const ProviderAccessPage = () => {
  usePageSEO();

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${BASE}/provider-access#webpage`,
      url: `${BASE}/provider-access`,
      name: "Provider Access | Verified-Provider Pricing for Research Peptides",
      description:
        "Verified-provider account program through Alpha BioMed Labs. Institutional-level pricing on research-grade peptides for med spas, clinics, telehealth platforms, compounding pharmacy partners, and research labs.",
      inLanguage: "en-US",
      isPartOf: { "@id": `${BASE}/#website` },
      breadcrumb: { "@id": `${BASE}/provider-access#breadcrumb` },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${BASE}/provider-access#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
        { "@type": "ListItem", position: 2, name: "Provider Access", item: `${BASE}/provider-access` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "@id": `${BASE}/provider-access#howto`,
      name: "How to apply for verified-provider access",
      description:
        "Three-step process to apply for Alpha BioMed Labs verified-provider access through PeptidesForMedSpas.",
      step: HOW_IT_WORKS.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.title,
        text: s.body,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${BASE}/provider-access#faq`,
      mainEntity: [
        {
          "@type": "Question",
          name: "Who qualifies for verified-provider access?",
          acceptedAnswer: { "@type": "Answer", text: "Med spas under licensed medical direction, aesthetic and longevity clinics, telehealth platforms with licensed prescribers, 503A/503B compounding pharmacy partners, academic and contract research labs, and wellness practices structured under their state's medical-direction rules. Final eligibility is determined by Alpha BioMed Labs during the verification step." }
        },
        {
          "@type": "Question",
          name: "Is there a fee to apply?",
          acceptedAnswer: { "@type": "Answer", text: "Application is free. Pricing, account terms, and eligibility decisions are made by Alpha BioMed Labs." }
        },
        {
          "@type": "Question",
          name: "Are these products FDA-approved drugs?",
          acceptedAnswer: { "@type": "Answer", text: "No. All materials are research chemicals supplied for laboratory research purposes only. Not for human or animal use. Not FDA-approved drugs." }
        },
        {
          "@type": "Question",
          name: "Does PeptidesForMedSpas earn a referral fee?",
          acceptedAnswer: { "@type": "Answer", text: "Yes — we may receive a referral credit when you create an Alpha BioMed Labs account through our link. This is disclosed inline on the page in compliance with FTC endorsement guidelines. The price you pay is set by Alpha BioMed Labs and is not affected by the referral relationship." }
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#fffaf5]" data-testid="provider-access-page">
      <Header />
      <JsonLd id="provider-access-jsonld" data={jsonLd} />

      {/* Hero */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-[#fdf2f4] to-[#fffaf5]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e8c5b8] rounded-full mb-5">
            <Lock className="w-3.5 h-3.5 text-[#b89a6d]" />
            <span className="text-xs uppercase tracking-wider text-[#b89a6d] font-semibold">
              Verified Provider Program
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3a2a26] mb-6 leading-tight" data-testid="provider-access-h1">
            Institutional pricing for
            <span className="block bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent">
              verified providers
            </span>
          </h1>
          <p className="text-base md:text-lg text-[#5a4a40] max-w-3xl leading-relaxed mb-8">
            Apply for verified-provider access through <strong className="text-[#3a2a26]">Alpha BioMed Labs</strong> to obtain
            institutional-level account credentials and authorized pricing for research-grade materials.
            Multi-unit pricing tiers offer significant savings versus single-unit retail — designed for
            practices, clinics, telehealth platforms, compounding pharmacy partners, and research labs
            ordering at volume.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="provider-access-hero-apply"
              onClick={TRACK("hero")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
            >
              Apply for Provider Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <span className="text-sm text-[#a09186]">
              ~5 minutes · free to apply · review by Alpha BioMed Labs
            </span>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-2">What verified providers get</h2>
          <p className="text-[#7a6b62] mb-8">Built for practices and labs ordering research-grade materials at volume.</p>
          <div className="grid md:grid-cols-2 gap-6" data-testid="provider-benefits">
            {BENEFITS.map((b, i) => (
              <article
                key={b.title}
                className="bg-white border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`provider-benefit-${i}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 bg-[#fdf2f4] rounded-lg flex items-center justify-center">
                    <b.icon className="w-5 h-5 text-[#b89a6d]" />
                  </div>
                  <h3 className="text-base md:text-lg font-bold text-[#3a2a26]">{b.title}</h3>
                </div>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{b.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-8">How it works</h2>
          <div className="grid md:grid-cols-3 gap-5" data-testid="provider-how-it-works">
            {HOW_IT_WORKS.map((s, i) => (
              <div
                key={s.n}
                className="bg-[#fffaf5] border border-[#f0d8d4] rounded-2xl p-7"
                data-testid={`provider-step-${i}`}
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-[#d4a5ad] to-[#b89a6d] bg-clip-text text-transparent mb-3">
                  {s.n}
                </div>
                <h3 className="text-base md:text-lg font-bold text-[#3a2a26] mb-2">{s.title}</h3>
                <p className="text-sm md:text-base text-[#5a4a40] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-12 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3a2a26] mb-3">Who qualifies</h2>
          <p className="text-[#7a6b62] mb-6">
            Final eligibility is determined by Alpha BioMed Labs during verification.
          </p>
          <div className="bg-white border border-[#f0d8d4] rounded-2xl p-7" data-testid="provider-eligibility">
            <ul className="grid sm:grid-cols-2 gap-3">
              {ELIGIBLE.map((e) => (
                <li key={e} className="flex items-start gap-2 text-sm md:text-base text-[#5a4a40]">
                  <CheckCircle2 className="w-5 h-5 text-[#b89a6d] mt-0.5 flex-shrink-0" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#fdf2f4] via-[#faeae5] to-[#f7e0d2] border border-[#e8c5b8] p-10 md:p-14 text-center">
            <Sparkles className="w-7 h-7 text-[#b89a6d] mx-auto mb-4" />
            <h2 className="text-2xl md:text-4xl font-bold text-[#3a2a26] mb-4">
              Start your verification today
            </h2>
            <p className="text-base md:text-lg text-[#5a4a40] max-w-2xl mx-auto mb-8 leading-relaxed">
              Complete the form below to initiate your account verification process with Alpha BioMed Labs.
              Free to apply — pricing and eligibility determined during review.
            </p>
            <a
              href={APPLY_URL}
              target="_blank"
              rel="noopener noreferrer nofollow sponsored"
              data-testid="provider-access-final-apply"
              onClick={TRACK("final-cta")}
              className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-[#d4a5ad] to-[#c4949c] hover:from-[#c4949c] hover:to-[#b48390] text-white font-semibold rounded-full shadow-lg shadow-[#d4a5ad]/30 transition-all"
            >
              Apply for Provider Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <p className="mt-4 text-xs text-[#a09186] font-mono">
              alphabiomedlabs.com/pages/register
            </p>
          </div>
        </div>
      </section>

      {/* Compliance / Disclosure */}
      <section className="py-10 bg-[#fffaf5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#fdf2f4] border border-[#e8c5b8] rounded-xl p-6">
            <h2 className="text-base font-bold text-[#3a2a26] mb-3 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#b89a6d]" />
              Disclosure & compliance
            </h2>
            <p className="text-sm text-[#5a4a40] leading-relaxed mb-3">
              <strong>Affiliate disclosure (FTC).</strong> PeptidesForMedSpas may receive a referral
              credit when you create an Alpha BioMed Labs account through links on this page. Pricing,
              account terms, and eligibility decisions are made by Alpha BioMed Labs. The price you pay
              is not affected by the referral relationship.
            </p>
            <p className="text-sm text-[#5a4a40] leading-relaxed">
              <strong>Research-only.</strong> All materials are research chemicals supplied for laboratory
              research purposes only — not for human or animal use, not FDA-approved drugs, not intended
              to diagnose, treat, cure, or prevent any disease. See our{" "}
              <Link to="/compliance" className="text-[#b89a6d] underline hover:text-[#a08560]" data-testid="provider-to-compliance">
                Compliance &amp; Regulatory page
              </Link>{" "}
              for our full posture.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProviderAccessPage;
